import React, { useMemo, useState, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Tab, Tabs, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


interface LinkDialogProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (url: string) => void;
	url: string;
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

const LinkDialog: React.FC<LinkDialogProps> = ({
	open,
	onClose,
	onSubmit,
	url,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const [urlString, setUrlString] = useState<string>("");
	const [value, setValue] = useState(0);
	const [error, setError] = useState<{ show: boolean; message: string }>({
		show: false,
		message: "",
	});

	useMemo(() => {
		if (url.startsWith("http://") || url.startsWith("https://")) {
			setValue(1);
		} else {
			setValue(0);
		}
		setUrlString(url);
	}, [url]);

	const handleSubmit = () => {
		if (value === 1) {
			const urlPattern =
				/^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;
			if (!urlPattern.test(urlString)) {
				setError({
					show: true,
					message: "Please enter a valid URL. http:// or https:// is required.",
				});
				return;
			}
		}
		onSubmit(urlString);
		setUrlString(urlString);
		onClose();
	};

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
		setError({ show: false, message: "" });
	};

	return (
		<Dialog fullWidth open={open} onClose={onClose}>
			<DialogTitle>
				<Translate String="Auto.Shared.Forms.SatosEditor.LinkDialog.L95C17.EnterURL">Enter URL</Translate>
				<IconButton aria-label={GetLanguageValueAsString(translation, "Auto.Shared.Forms.SatosEditor.LinkDialog.L97C28.Close", "Close")} onClick={onClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<Box sx={{ width: "100%" }}>
				<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
					<Tabs
						value={value}
						onChange={handleChange}
						aria-label={GetLanguageValueAsString(translation, "Auto.Shared.Forms.SatosEditor.LinkDialog.L106C18.BasicTabsExample", "basic tabs example")}
					>
						<Tab
							label={
								<Translate String="Editor.LinkDialog.Internal">
									Internal Link
								</Translate>
							}
							{...a11yProps(0)}
						/>
						<Tab
							label={
								<Translate String="Editor.LinkDialog.External">
									External Link
								</Translate>
							}
							{...a11yProps(1)}
						/>
					</Tabs>
				</Box>
				<CustomTabPanel value={value} index={0}>
					<Typography variant="body2" color="initial">
						<Translate String="Editor.LinkDialog.Internal.Link.Description">
							Internal link to a internal page in SATOS. Example: /procedure
						</Translate>
					</Typography>
				</CustomTabPanel>
				<CustomTabPanel value={value} index={1}>
					<Typography variant="body2" color="initial">
						<Translate String="Editor.LinkDialog.External.Link.Description">
							External link to a external page. Example: https://example.com
						</Translate>
					</Typography>
				</CustomTabPanel>
			</Box>
			<DialogContent>
				<TextField
					autoFocus
					margin="dense"
					label={GetLanguageValueAsString(translation, "Auto.Shared.Forms.SatosEditor.LinkDialog.L145C12.URL", "URL")}
					type="url"
					fullWidth
					placeholder={value === 0 ? "/procedure" : "https://example.com"}
					size="small"
					variant="outlined"
					value={urlString}
					onChange={(e) => setUrlString(e.target.value)}
				/>
				{error.show && (
					<Typography variant="caption" color="error">
						<Translate String="Editor.LinkDialog.External.Link.Error">
							{error.message}
						</Translate>
					</Typography>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="error">
					<Translate String="Auto.Shared.Forms.SatosEditor.LinkDialog.L163C45.Cancel">Cancel</Translate>
				</Button>
				<Button onClick={handleSubmit} color="success">
					<Translate String="Auto.Shared.Forms.SatosEditor.LinkDialog.L166C52.Submit">Submit</Translate>
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default LinkDialog;
